import { useState, useEffect } from 'react';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Checkbox } from '@/core/components/ui/checkbox';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Language } from '../../types/translation-types';
import {
  CreateLanguageRequest,
  UpdateLanguageRequest,
} from '../../types/language-management-types';

interface LanguageFormProps {
  language?: Language | null;
  onSubmit: (
    data: CreateLanguageRequest | UpdateLanguageRequest,
    copyFromLanguageCode?: string,
    copySoundsFromLanguageCode?: string
  ) => Promise<void>;
  isSubmitting: boolean;
  error?: string | null;
  isEditMode: boolean;
}

interface FormData {
  languageCode: string;
  languageName: string;
  nativeName: string;
  isRtl: boolean;
  sortOrder: number;
  isActive: boolean;
  imageUrl: string;
  localFormat: string;
  operatorName: string;
  copyFromParent: boolean;
  copySoundsFromParent: boolean;
}

export function LanguageForm({
  language,
  onSubmit,
  isSubmitting,
  error,
  isEditMode,
}: LanguageFormProps) {
  const [formData, setFormData] = useState<FormData>({
    languageCode: '',
    languageName: '',
    nativeName: '',
    isRtl: false,
    sortOrder: 0,
    isActive: true,
    imageUrl: '',
    localFormat: '',
    operatorName: '',
    copyFromParent: false,
    copySoundsFromParent: false,
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Initialize form data when language changes
  useEffect(() => {
    if (language) {
      setFormData({
        languageCode: language.languageCode,
        languageName: language.languageName,
        nativeName: language.nativeName || '',
        isRtl: language.isRtl,
        sortOrder: language.sortOrder,
        isActive: language.isActive,
        imageUrl: language.imageUrl || '',
        localFormat: language.localFormat || '',
        operatorName: '',
        copyFromParent: false,
        copySoundsFromParent: false,
      });
    } else {
      setFormData({
        languageCode: '',
        languageName: '',
        nativeName: '',
        isRtl: false,
        sortOrder: 0,
        isActive: true,
        imageUrl: '',
        localFormat: '',
        operatorName: '',
        copyFromParent: false,
        copySoundsFromParent: false,
      });
    }
    setValidationErrors([]);
  }, [language]);

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!isEditMode) {
      if (!formData.languageCode.trim()) {
        errors.push('Language code is required');
      } else if (formData.languageCode.length > 10) {
        errors.push('Language code must be 10 characters or less');
      } else if (!/^[a-z][a-z0-9-]*$/i.test(formData.languageCode)) {
        errors.push('Language code must start with a letter and contain only letters, numbers, and hyphens');
      }

      if (formData.operatorName.trim()) {
        if (formData.operatorName.includes('#')) {
          errors.push('Operator name cannot contain "#"');
        } else if (!/^[a-z0-9-]+$/i.test(formData.operatorName.trim())) {
          errors.push('Operator name must contain only letters, numbers, and hyphens');
        } else if (formData.operatorName.trim().length > 30) {
          errors.push('Operator name must be 30 characters or less');
        }
      }
    }

    if (!formData.languageName.trim()) {
      errors.push('Language name is required');
    } else if (formData.languageName.length > 100) {
      errors.push('Language name must be 100 characters or less');
    }

    if (!formData.nativeName.trim()) {
      errors.push('Native name is required');
    } else if (formData.nativeName.length > 100) {
      errors.push('Native name must be 100 characters or less');
    }

    if (formData.sortOrder < 0) {
      errors.push('Sort order must be 0 or greater');
    }

    if (!formData.localFormat.trim()) {
      errors.push('Local format is required');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    if (isEditMode) {
      const updateData: UpdateLanguageRequest = {
        languageCode: formData.languageCode,
        languageName: formData.languageName.trim(),
        nativeName: formData.nativeName.trim(),
        isRtl: formData.isRtl,
        sortOrder: formData.sortOrder,
        isActive: formData.isActive,
        imageUrl: formData.imageUrl.trim() || undefined,
        localFormat: formData.localFormat.trim(),
      };
      await onSubmit(updateData);
    } else {
      const baseCode = formData.languageCode.toLowerCase().trim();
      const operatorName = formData.operatorName.toLowerCase().trim();
      const composedCode = operatorName ? `${baseCode}#${operatorName}` : baseCode;

      const createData: CreateLanguageRequest = {
        languageCode: composedCode,
        languageName: formData.languageName.trim(),
        nativeName: formData.nativeName.trim(),
        isRtl: formData.isRtl,
        sortOrder: formData.sortOrder,
        isActive: formData.isActive,
        imageUrl: formData.imageUrl.trim() || undefined,
        localFormat: formData.localFormat.trim(),
      };
      const copyFromLanguageCode =
        operatorName && formData.copyFromParent ? baseCode : undefined;
      const copySoundsFromLanguageCode =
        operatorName && formData.copySoundsFromParent ? baseCode : undefined;
      await onSubmit(createData, copyFromLanguageCode, copySoundsFromLanguageCode);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" id="language-form">
      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc list-inside">
              {validationErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* API Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Language Code */}
      <div className="space-y-2">
        <Label htmlFor="languageCode">
          Language Code <span className="text-red-500">*</span>
        </Label>
        <Input
          id="languageCode"
          value={formData.languageCode}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, languageCode: e.target.value }))
          }
          placeholder="e.g., en, fr, de"
          disabled={isEditMode || isSubmitting}
          maxLength={10}
          className="font-mono"
        />
        <p className="text-xs text-gray-500">
          Lowercase code, max 10 characters (e.g., en, en-gb, fr)
        </p>
      </div>

      {/* Operator Name (create mode only) — composes a per-operator compound language code */}
      {!isEditMode && (
        <div className="space-y-2">
          <Label htmlFor="operatorName">Operator Name (optional)</Label>
          <Input
            id="operatorName"
            value={formData.operatorName}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, operatorName: e.target.value }))
            }
            placeholder="e.g., paz"
            disabled={isSubmitting}
            maxLength={30}
            className="font-mono"
          />
          <p className="text-xs text-gray-500">
            If set, creates a client-specific language variant. Resulting code:{' '}
            <span className="font-mono">
              {formData.languageCode.toLowerCase().trim() || 'xx'}#
              {formData.operatorName.toLowerCase().trim() || 'operator'}
            </span>
          </p>

          {formData.operatorName.trim() && (
            <div className="flex items-center space-x-2 pt-1">
              <Checkbox
                id="copyFromParent"
                checked={formData.copyFromParent}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, copyFromParent: checked === true }))
                }
                disabled={isSubmitting}
              />
              <Label htmlFor="copyFromParent" className="cursor-pointer font-normal">
                Copy all translations from parent language{' '}
                <span className="font-mono">
                  {formData.languageCode.toLowerCase().trim() || 'xx'}
                </span>
              </Label>
            </div>
          )}

          {formData.operatorName.trim() && (
            <div className="flex items-center space-x-2 pt-1">
              <Checkbox
                id="copySoundsFromParent"
                checked={formData.copySoundsFromParent}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, copySoundsFromParent: checked === true }))
                }
                disabled={isSubmitting}
              />
              <Label htmlFor="copySoundsFromParent" className="cursor-pointer font-normal">
                Copy sound files from parent language{' '}
                <span className="font-mono">
                  {formData.languageCode.toLowerCase().trim() || 'xx'}
                </span>
              </Label>
            </div>
          )}
        </div>
      )}

      {/* Language Name */}
      <div className="space-y-2">
        <Label htmlFor="languageName">
          Language Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="languageName"
          value={formData.languageName}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, languageName: e.target.value }))
          }
          placeholder="e.g., English, French, German"
          disabled={isSubmitting}
          maxLength={100}
        />
        <p className="text-xs text-gray-500">
          Display name in English
        </p>
      </div>

      {/* Native Name */}
      <div className="space-y-2">
        <Label htmlFor="nativeName">
          Native Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="nativeName"
          value={formData.nativeName}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, nativeName: e.target.value }))
          }
          placeholder="e.g., English, Français, Deutsch"
          disabled={isSubmitting}
          maxLength={100}
        />
        <p className="text-xs text-gray-500">
          Name in the language itself
        </p>
      </div>

      {/* Sort Order */}
      <div className="space-y-2">
        <Label htmlFor="sortOrder">Sort Order</Label>
        <Input
          id="sortOrder"
          type="number"
          min={0}
          value={formData.sortOrder}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              sortOrder: parseInt(e.target.value, 10) || 0,
            }))
          }
          disabled={isSubmitting}
          className="w-32"
        />
        <p className="text-xs text-gray-500">
          Lower numbers appear first in lists
        </p>
      </div>

      {/* Local Format */}
      <div className="space-y-2">
        <Label htmlFor="localFormat">Local Format <span className="text-red-500">*</span></Label>
        <Input
          id="localFormat"
          value={formData.localFormat}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, localFormat: e.target.value }))
          }
          placeholder="e.g., en-US, fr-FR"
          disabled={isSubmitting}
        />
        <p className="text-xs text-gray-500">
          Locale code for date/number formatting
        </p>
      </div>

      {/* Image URL */}
      <div className="space-y-2">
        <Label htmlFor="imageUrl">Image URL</Label>
        <Input
          id="imageUrl"
          value={formData.imageUrl}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, imageUrl: e.target.value }))
          }
          placeholder="https://..."
          disabled={isSubmitting}
        />
        <p className="text-xs text-gray-500">
          URL to flag or language icon (optional)
        </p>
      </div>

      {/* RTL Checkbox */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="isRtl"
          checked={formData.isRtl}
          onCheckedChange={(checked) =>
            setFormData((prev) => ({ ...prev, isRtl: checked === true }))
          }
          disabled={isSubmitting}
        />
        <Label htmlFor="isRtl" className="cursor-pointer">
          Right-to-Left (RTL)
        </Label>
      </div>

      {/* Active Checkbox */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="isActive"
          checked={formData.isActive}
          onCheckedChange={(checked) =>
            setFormData((prev) => ({ ...prev, isActive: checked === true }))
          }
          disabled={isSubmitting}
        />
        <Label htmlFor="isActive" className="cursor-pointer">
          Active
        </Label>
      </div>
    </form>
  );
}
